const ESC = "\x1B";
const GS = "\x1D";

// Default paper width in characters. 32 for 58mm paper, 48 for 80mm paper.
const DEFAULT_WIDTH = 32;

/**
 * Creates a Printer helper bound to a given paper width.
 * Usage: const Printer = createPrinter(32);
 */
export function createPrinter(width = DEFAULT_WIDTH) {
  return {
    width,

    initialize() {
      return ESC + "@";
    },

    // --- Alignment ---
    left() {
      return ESC + "a" + "\x00";
    },
    center() {
      return ESC + "a" + "\x01";
    },
    right() {
      return ESC + "a" + "\x02";
    },

    // --- Emphasis / size ---
    boldOn() {
      return ESC + "E" + "\x01";
    },
    boldOff() {
      return ESC + "E" + "\x00";
    },
    doubleSize() {
      return GS + "!" + "\x11";
    },
    normalSize() {
      return GS + "!" + "\x00";
    },
    underlineOn() {
      return ESC + "-" + "\x01";
    },
    underlineOff() {
      return ESC + "-" + "\x00";
    },

    // --- Feed / cut ---
    feed(lines = 1) {
      return ESC + "d" + String.fromCharCode(lines);
    },
    cut(partial = false) {
      // GS V 0 = full cut, GS V 1 = partial cut
      return GS + "V" + String.fromCharCode(partial ? 1 : 0);
    },

    // --- Text helpers ---
    divider(char = "-") {
      return char.repeat(this.width) + "\n";
    },
    line(text = "") {
      return text + "\n";
    },

    /**
     * Two-column line, left-aligned text on the left, right-aligned text
     * on the right, padded to fill `width`. Falls back to a wrapped
     * second line if the combined text can't fit on one row.
     */
    row(left, right, width = this.width) {
      const space = width - left.length - right.length;
      if (space < 1) {
        // Not enough room: put right-hand text on its own right-aligned line
        const pad = Math.max(width - right.length, 0);
        return `${left}\n${" ".repeat(pad)}${right}\n`;
      }
      return `${left}${" ".repeat(space)}${right}\n`;
    },

    /**
     * Wraps long text to the configured width, breaking on word
     * boundaries where possible so item names don't rely on firmware
     * wrapping.
     */
    wrap(text, width = this.width) {
      const words = text.split(" ");
      const lines = [];
      let current = "";

      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (candidate.length > width) {
          if (current) lines.push(current);
          // word itself longer than width; hard-break it
          if (word.length > width) {
            let remaining = word;
            while (remaining.length > width) {
              lines.push(remaining.slice(0, width));
              remaining = remaining.slice(width);
            }
            current = remaining;
          } else {
            current = word;
          }
        } else {
          current = candidate;
        }
      }
      if (current) lines.push(current);
      return lines.join("\n") + "\n";
    },

    /**
     * Formats a Naira amount using "N" instead of the ₦ glyph, since
     * most ESC/POS code pages don't include the Naira sign and raw
     * UTF-8 bytes will print as garbage on typical thermal printers.
     */
    money(amount) {
      const num = Number(amount) || 0;
      return `N${num.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    /**
     * Prints a raster bitmap via GS v 0.
     * `bitmap` must be a Uint8Array/Buffer of packed 1-bit pixel data,
     * already dithered/converted to monochrome (e.g. via a tool like
     * `sharp` + a floyd-steinberg dither, or a pre-baked bitmap asset).
     * widthBytes = ceil(widthPx / 8), heightPx = image height in pixels.
     */
    image(bitmap, widthBytes, heightPx) {
      const xL = widthBytes & 0xff;
      const xH = (widthBytes >> 8) & 0xff;
      const yL = heightPx & 0xff;
      const yH = (heightPx >> 8) & 0xff;
      const header = GS + "v0" + "\x00" + String.fromCharCode(xL, xH, yL, yH);
      // Caller is responsible for concatenating header + bitmap bytes
      // at the byte-buffer stage (not as a JS string) to avoid
      // corrupting binary data through string concatenation.
      return { header, bitmap };
    },
  };
}

// Default export kept for drop-in compatibility with existing imports
// that expect a ready-to-use Printer object at 32-char width.
export const Printer = createPrinter(DEFAULT_WIDTH);
